import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

const STEP_IMPORTS = {
  UPLOAD_ARTWORK: () => import('./steps/UploadArtwork'),
  // Same component; it branches on the step code to add the review pitch.
  UPLOAD_ARTWORK_REVIEW: () => import('./steps/UploadArtwork'),
  PRIMARY_INTENT: () => import('./steps/PrimaryIntent'),
  WEB_PRESENCE: () => import('./steps/WebPresence'),
  SOCIAL_LINKS: () => import('./steps/SocialLinks'),
  NAME: () => import('./steps/Name'),
  ART_CATEGORIES: () => import('./steps/ArtCategories'),
  AVATAR: () => import('./steps/Avatar'),
  CAREER_STAGE: () => import('./steps/CareerStage'),
  PRACTICE_FREQUENCY: () => import('./steps/PracticeFrequency'),
  BIO: () => import('./steps/Bio'),
  FOLLOW_ARTISTS: () => import('./steps/FollowArtists'),
  JOIN_COMMUNITIES: () => import('./steps/JoinCommunities'),
  WELCOME_POST: () => import('./steps/WelcomePost'),
  MOCKUPS_FOR_YOU: () => import('./steps/MockupsForYou'),
  TOP_THREADS: () => import('./steps/TopThreads'),
  PHONE_NUMBER: () => import('./steps/PhoneNumber'),
  // Content steps — no payload, nothing written. See social-proof-data.ts.
  SOCIAL_PROOF_LIST: () => import('./steps/SocialProofList'),
  SOCIAL_PROOF_CARDS: () => import('./steps/SocialProofCards'),
  SOCIAL_PROOF_WALL: () => import('./steps/SocialProofWall'),
  SOCIAL_PROOF_HIGHLIGHTS: () => import('./steps/SocialProofHighlights'),
  ARTIST_SPOTLIGHT: () => import('./steps/ArtistSpotlight'),
  EXPLAINER: () => import('./steps/Explainer'),
  MILESTONE: () => import('./steps/Milestone'),
  VIDEO: () => import('./steps/VideoStep'),
  FINALE_RECEIPT: () => import('./steps/FinaleReceipt'),
  FINALE_PROFILE: () => import('./steps/FinaleProfile'),
  FINALE_NEXT_ACTION: () => import('./steps/FinaleNextAction'),
  FINALE_SPOTLIGHT: () => import('./steps/FinaleSpotlight'),
  // Completion steps — render AFTER the wizard completes (outcome, welcomeCompletedAt,
  // conversion pixel all fired at the last normal step). An optional bonus lap.
  COMPLETION_CRITIQUE: () => import('./steps/CompletionCritique'),
  COMPLETION_REVIEW: () => import('./steps/CompletionReview'),
  COMPLETION_COMMUNITY: () => import('./steps/CompletionCommunity'),
  COMPLETION_PROFILE: () => import('./steps/CompletionProfile'),
} as const;

type StepCode = keyof typeof STEP_IMPORTS;

export const STEP_COMPONENT_REGISTRY: Record<StepCode, LazyExoticComponent<ComponentType>> = Object.fromEntries(
  (Object.entries(STEP_IMPORTS) as [StepCode, (typeof STEP_IMPORTS)[StepCode]][]).map(([key, importer]) => [
    key,
    lazy(importer as () => Promise<{ default: ComponentType }>),
  ])
) as Record<StepCode, LazyExoticComponent<ComponentType>>;

// Kick off all step chunks in parallel so navigating between steps doesn't
// trip the Suspense boundary. Safe to call repeatedly — module imports are
// memoized by the bundler.
export function preloadAllSteps(): void {
  Object.values(STEP_IMPORTS).forEach((importer) => {
    importer().catch(() => {
      // Swallow preload failures — the lazy component will retry on render.
    });
  });
}

/**
 * Steps that need more than the 620px column. A lookup rather than a ternary because the
 * content steps keep adding to it — a testimonial list wants the extra room for its quote
 * rows the same way the artist grid does.
 *
 * Lives here rather than in WelcomeWizard because the admin preview lays steps out too, and
 * a preview that sizes them differently is a preview that lies.
 */
export const STEP_MAX_WIDTHS: Record<string, string> = {
  FOLLOW_ARTISTS: '720px',
  SOCIAL_PROOF_LIST: '720px',
  SOCIAL_PROOF_CARDS: '800px',
  SOCIAL_PROOF_WALL: '800px',
  SOCIAL_PROOF_HIGHLIGHTS: '780px',
  ARTIST_SPOTLIGHT: '720px',
  FINALE_SPOTLIGHT: '720px',
  VIDEO: '760px',
};

export const DEFAULT_STEP_MAX_WIDTH = '620px';

/**
 * A flow version may end on one of these instead of the original `Done` screen. They are
 * ordinary positional steps — this prefix exists only so a host can tell whether the
 * version it is running has an ending of its own.
 */
export const FINALE_PREFIX = 'FINALE_';

/**
 * Completion steps render after the wizard completes. The prefix is the client's cheap
 * mirror of the registry's `postCompletion` flag (FINALE_ precedent below).
 */
export const COMPLETION_PREFIX = 'COMPLETION_';

export function isCompletionStep(code: string | null | undefined): boolean {
  return Boolean(code?.startsWith(COMPLETION_PREFIX));
}

/** The version's own ending: the last step, if and only if that step is a FINALE_*. */
export function findFinaleStepCode(stepCodes: string[]): string | null {
  const last = stepCodes[stepCodes.length - 1];
  return last?.startsWith(FINALE_PREFIX) ? last : null;
}
